import type { FirebaseApp } from 'firebase/app';
import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { Analytics } from 'firebase/analytics';
import { createLogger } from './loggingService';

const logger = createLogger('Firebase');

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const ensureConfig = () => {
  if (!firebaseConfig.apiKey) {
    throw new Error('Missing Firebase configuration. Please check your environment variables.');
  }
  return firebaseConfig;
};

let appPromise: Promise<FirebaseApp> | null = null;
const getFirebaseApp = (): Promise<FirebaseApp> => {
  if (!appPromise) {
    appPromise = import('firebase/app').then(({ initializeApp, getApps }) => {
      const existing = getApps();
      if (existing.length) {
        return existing[0];
      }
      return initializeApp(ensureConfig());
    });
  }
  return appPromise;
};

let authPromise: Promise<Auth> | null = null;
const getFirebaseAuth = async (): Promise<Auth> => {
  if (!authPromise) {
    authPromise = getFirebaseApp().then(async app => {
      const { getAuth } = await import('firebase/auth');
      return getAuth(app);
    });
  }
  return authPromise;
};

let firestorePromise: Promise<Firestore> | null = null;
const getFirestoreDb = async (): Promise<Firestore> => {
  if (!firestorePromise) {
    firestorePromise = getFirebaseApp().then(async app => {
      const { getFirestore } = await import('firebase/firestore');
      return getFirestore(app);
    });
  }
  return firestorePromise;
};

const loadAnalytics = async (): Promise<Analytics | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const [analyticsModule, app] = await Promise.all([
      import('firebase/analytics'),
      getFirebaseApp(),
    ]);
    const supported = await analyticsModule.isSupported();
    if (!supported) {
      return null;
    }
    return analyticsModule.getAnalytics(app);
  } catch (error) {
    logger.warn('Firebase Analytics failed to initialize', { error: error instanceof Error ? error.message : String(error) });
    return null;
  }
};

export { getFirebaseApp, getFirebaseAuth, getFirestoreDb, loadAnalytics };
